import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  CheckCircle2,
  Phone,
  Mail,
  MapPin,
  Clock,
  ArrowRight,
  MessageSquare,
} from 'lucide-react';
import { contactInfo, processSteps } from '../data';
import { Button } from '../components/ui/button';
import { Breadcrumb } from '../components/Breadcrumb';
import SEO from '../components/SEO';

const ThankYou = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const conversionType = location.state?.type;
  const hasSubmitted = Boolean(conversionType);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (!hasSubmitted) {
        navigate('/', { replace: true });
      }
    }
  }, [hasSubmitted, navigate]);

  if (!hasSubmitted) return null;

  const isQuotation = conversionType !== 'contact';

  return (
    <div>
      <SEO
        title="Thank You"
        description="Thank you for contacting Aashi Constructions."
        robots="noindex, nofollow"
      />
      <Breadcrumb items={[{ label: 'Thank You' }]} />

      {/* Hero Section */}
      <section className="relative h-72 sm:h-80 md:h-96 flex items-center justify-center overflow-hidden">
        <img
          src="https://images.pexels.com/photos/1571459/pexels-photo-1571459.jpeg"
          alt="A completed Aashi Constructions home"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/60 to-black/75" />

        <div className="relative z-10 text-center px-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-brand-600 rounded-full flex items-center justify-center mx-auto mb-5 sm:mb-6 shadow-2xl">
            <CheckCircle2 className="w-9 h-9 sm:w-12 sm:h-12 text-white" />
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-3 sm:mb-4">
            {isQuotation ? 'Thank you for your enquiry' : 'Thank you for reaching out'}
          </h1>

          <p className="text-sm sm:text-base md:text-lg text-white/90 max-w-2xl mx-auto">
            {isQuotation
              ? 'Your quotation request is now with our team. We will get back to you shortly.'
              : 'Your message is now with our team. A member of our team will get back to you shortly.'}
          </p>
        </div>
      </section>

      {/* Next Steps Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-brand-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
              What Happens Next
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Here is how we move forward from this point
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-11">
            <div className="text-center relative">
              <div className="w-20 h-20 bg-gradient-to-br from-brand-600 to-brand-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <MessageSquare className="w-9 h-9 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">We Reach Out</h3>
              <p className="text-gray-600 leading-relaxed">
                A dedicated project advisor calls you within 24 hours to understand your requirements in detail.
              </p>
              <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-brand-600 to-transparent -ml-24" />
            </div>

            <div className="text-center relative">
              <div className="w-20 h-20 bg-gradient-to-br from-brand-600 to-brand-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Clock className="w-9 h-9 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Free Consultation</h3>
              <p className="text-gray-600 leading-relaxed">
                We visit your site or meet at our office to assess the plot, discuss design, and refine your budget.
              </p>
              <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-brand-600 to-transparent -ml-24" />
            </div>

            <div className="text-center relative">
              <div className="w-20 h-20 bg-gradient-to-br from-brand-600 to-brand-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <CheckCircle2 className="w-9 h-9 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Detailed Proposal</h3>
              <p className="text-gray-600 leading-relaxed">
                You receive a comprehensive quotation with transparent pricing, timelines, and the scope of work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Details Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
                Need Something Sooner?
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-10 leading-relaxed">
                We do not like to keep you waiting. Reach out directly and we will prioritise your enquiry.
              </p>

              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Call Us</h3>
                    {contactInfo.phones.map((phone) => (
                      <a
                        key={phone}
                        href={`tel:${phone.replace(/\s/g, '')}`}
                        className="text-gray-600 hover:text-brand-600 transition-colors duration-200 block"
                      >
                        {phone}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center flex-fhrink-0">
                    <Mail className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Email Us</h3>
                    {contactInfo.emails.map((email) => (
                      <a
                        key={email}
                        href={`mailto:${email}`}
                        className="text-gray-600 hover:text-brand-600 transition-colors duration-200 block"
                      >
                        {email}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Visit Our Office</h3>
                    <p className="text-gray-600">{contactInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center flex-fhrink-0">
                    <Clock className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Office Hours</h3>
                    <p className="text-gray-600">{contactInfo.hours}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Explore Projects */}
            <div>
              <div className="inline-block px-4 py-2 bg-brand-100 text-brand-800 rounded-full text-sm font-semibold mb-6">
                While You Wait
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-4">
                Explore Our Work
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                Have a look at the homes we have built across Bangalore and the packages we offer, so you can start
                planning with real numbers in mind.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-10">
                <div className="text-center p-4 sm:p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100">
                  <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-brand-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Completed Projects</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Browse homes we have delivered across Karnataka
                  </p>
                </div>

                <div className="text-center p-4 sm:p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100">
                  <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-brand-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Construction Packages</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Compare plans and pricing for your build
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <Link to="/projects" className="block">
                  <Button className="w-full bg-brand-600 hover:bg-brand-700 text-white">
                    View Our Projects
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/packages" className="block">
                  <Button
                    variant="outline"
                    className="w-full border-brand-600 text-brand-600 hover:bg-brand-50"
                  >
                    Explore Packages
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Explore Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-white mb-4">
            Built on Trust, Delivered on Time
          </h2>
          <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            {processSteps[0].description} is where every project with us begins. Your enquiry has already started that
            conversation.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-brand-600 to-brand-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/projects">
              <Button className="w-full sm:w-auto bg-brand-600 hover:bg-brand-500 text-white">
                View Our Projects
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/packages">
              <Button
                variant="outline"
                className="w-full sm:w-auto border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
              >
                Explore Packages
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThankYou;